import * as React from "react";
import { observer } from "mobx-react-lite";
import TaskStatusesVisualStore from "../stores/TaskStatusesVisualStore";
import { Form, Input, Tag, Tree } from "antd";
import { FlowEditorSelection, TaskStatus } from "../types";

type Props = {
    store: TaskStatusesVisualStore;
    selection: FlowEditorSelection;
    isCustomMode: boolean;
};

const boldLabelStyle: React.CSSProperties = {
    fontWeight: 700,
};

type CheckedActionKeys =
    | React.Key[]
    | {
          checked: React.Key[];
      };

const getSelectedStatusId = (selection: FlowEditorSelection) =>
    selection.item?.id ??
    selection.item?.data?.id ??
    selection.item?.getData?.()?.id;

const getCheckedActionIds = (checkedKeys: CheckedActionKeys) =>
    (Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked).map(
        String,
    );

const FlowNodeEditor: React.FC<Props> = ({ store, selection }) => {
    const selectedStatusId = getSelectedStatusId(selection);
    const selectedStatus = store.taskStatuses.find(
        (status) => status.id === selectedStatusId,
    );

    const nextStatuses = React.useMemo(() => {
        if (!selectedStatus?.transitions) {
            return [];
        }

        const mappedStatuses = selectedStatus.transitions
            .map((transition) =>
                store.taskStatuses.find(
                    (candidate) => candidate.id === transition.targetStatusId,
                ),
            )
            .filter(
                (candidate): candidate is TaskStatus =>
                    !!candidate &&
                    candidate.isSubtaskStatus ===
                        selectedStatus.isSubtaskStatus,
            );

        return mappedStatuses.filter(
            (candidate, index) =>
                mappedStatuses.findIndex(
                    (status) => status.id === candidate.id,
                ) === index,
        );
    }, [selectedStatus, store.taskStatuses]);

    const hasActionDefinitions = store.actionDefinitions.length > 0;
    const handleAllowedActionsChange = React.useCallback(
        (checkedKeys: CheckedActionKeys) => {
            if (!selectedStatus) {
                return;
            }

            selectedStatus.allowedActions = getCheckedActionIds(checkedKeys);
        },
        [selectedStatus],
    );

    return (
        <>
            <Form.Item
                label={
                    <span className="dialog-field-label" style={boldLabelStyle}>
                        Name
                    </span>
                }
                name="name"
            >
                <Input data-id="input-name" readOnly />
            </Form.Item>
            <Form.Item
                label={
                    <span className="dialog-field-label" style={boldLabelStyle}>
                        Code
                    </span>
                }
                name="code"
            >
                <Input data-id="input-code" readOnly />
            </Form.Item>
            <Form.Item
                label={
                    <span className="dialog-field-label" style={boldLabelStyle}>
                        Next statuses
                    </span>
                }
            >
                {nextStatuses.length > 0 ? (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {nextStatuses.map((status) => (
                            <Tag
                                key={status.id}
                                style={{
                                    marginInlineEnd: 0,
                                    borderRadius: 999,
                                    background: "#F5F7FB",
                                    border: "1px solid #C9D3E5",
                                    color: "#2F4D8F",
                                    fontWeight: 600,
                                    paddingInline: 10,
                                    paddingBlock: 2,
                                }}
                            >
                                {status.name}
                            </Tag>
                        ))}
                    </div>
                ) : (
                    <span style={{ color: "#8C8C8C" }}>None</span>
                )}
            </Form.Item>
            <Form.Item
                label={
                    <span className="dialog-field-label" style={boldLabelStyle}>
                        Available actions
                    </span>
                }
                name="allowedActions"
                valuePropName="checkedKeys"
                trigger="onCheck"
            >
                {hasActionDefinitions ? (
                    <Tree
                        checkable
                        style={{ overflowY: "scroll", marginLeft: "-24px" }}
                        treeData={store.actionDefinitions.map((x) => ({
                            title: x.title || x.name,
                            key: x.id,
                        }))}
                        onCheck={handleAllowedActionsChange}
                    />
                ) : (
                    <div
                        style={{
                            border: "1px dashed #C9D3E5",
                            borderRadius: 12,
                            padding: "20px 24px",
                            textAlign: "center",
                            color: "#3B5174",
                            background: "#FFFFFF",
                        }}
                    >
                        <div
                            style={{
                                fontWeight: 700,
                                fontSize: 16,
                                color: "#102A56",
                                marginBottom: 8,
                            }}
                        >
                            No actions available
                        </div>
                        <div style={{ fontSize: 14, lineHeight: "22px" }}>
                            This project does not have any configured actions
                            yet.
                        </div>
                    </div>
                )}
            </Form.Item>
        </>
    );
};

export default observer(FlowNodeEditor);
